import { useState } from 'react';
import { useApp } from '../App';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

export default function VoiceInvoice() {
  const { user, setPage } = useApp();
  const { t } = useI18n();
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState(null);

  function startListening() {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setError('Голосовой ввод не поддерживается в этом браузере');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.lang = 'ru-RU';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setListening(true);
      setError(null);
    };

    recognition.onresult = (event) => {
      const text = event.results[0][0].transcript;
      setTranscript(text);
      parseVoiceCommand(text);
    };

    recognition.onerror = (event) => {
      setError(`Ошибка: ${event.error}`);
      setListening(false);
    };

    recognition.onend = () => {
      setListening(false);
    };

    recognition.start();
  }

  function parseVoiceCommand(text) {
    const lower = text.toLowerCase();
    
    // Parse amount
    let amount = null;
    const amountMatch = lower.match(/(\d+(?:[.,]\d+)?)\s*(злот|pln|евро|eur|доллар|usd|тысяч)/);
    if (amountMatch) {
      amount = parseFloat(amountMatch[1].replace(',', '.'));
      if (lower.includes('тысяч')) amount *= 1000;
    }
    
    // Parse currency
    let currency = 'PLN';
    if (lower.includes('евро') || lower.includes('eur')) currency = 'EUR';
    if (lower.includes('доллар') || lower.includes('usd')) currency = 'USD';
    
    // Parse client name (after "для", "клиенту", "фирме")
    let clientName = '';
    const clientMatch = lower.match(/(?:для|клиенту|фирме|компании)\s+([а-яa-z\s]+?)(?:\s+за|\s+на|\s*$)/i);
    if (clientMatch) {
      clientName = clientMatch[1].trim();
    }
    
    // Parse service (after "за")
    let serviceName = '';
    const serviceMatch = lower.match(/за\s+(.+?)(?:\s+на сумму|\s+в размере|\s*\d|\s*$)/i);
    if (serviceMatch) {
      serviceName = serviceMatch[1].trim();
    }
    
    setParsed({
      amount,
      currency,
      clientName: clientName.charAt(0).toUpperCase() + clientName.slice(1),
      serviceName: serviceName.charAt(0).toUpperCase() + serviceName.slice(1),
      raw: text,
    });
  }

  function createInvoice() {
    if (!parsed || !parsed.amount) {
      setError('Не удалось распознать сумму');
      return;
    }
    
    // Store data and redirect
    sessionStorage.setItem('voice_invoice', JSON.stringify(parsed));
    setPage('invoices_out');
  }

  const examples = [
    "Выстави проформу для компании ABC за консультацию на 500 злотых",
    "Проформа клиенту Иван за разработку сайта 2000 евро",
    "Счёт фирме XYZ за услуги 1500 PLN",
  ];

  return (
    <div className="page">
      <BackButton />
      <h1>🎙️ Голосовая проформа</h1>

      {/* Info */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        borderRadius: 12,
        padding: 20,
        marginBottom: 24,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>🎤</div>
        <p style={{ margin: 0, opacity: 0.9 }}>
          Просто скажите что нужно — система создаст проформу автоматически
        </p>
      </div>

      {/* Examples */}
      <div style={{
        background: 'rgba(255,255,255,0.05)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 24,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>💡 Примеры команд:</div>
        {examples.map((ex, i) => (
          <div key={i} style={{
            fontSize: 13,
            opacity: 0.8,
            padding: '4px 0',
            borderBottom: i < examples.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
          }}>
            "{ex}"
          </div>
        ))}
      </div>

      {/* Record Button */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <button
          onClick={startListening}
          disabled={listening}
          style={{
            width: 120,
            height: 120,
            borderRadius: '50%',
            border: 'none',
            background: listening
              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
              : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            cursor: listening ? 'default' : 'pointer',
            fontSize: 48,
            boxShadow: listening ? '0 0 30px rgba(239,68,68,0.5)' : '0 0 20px rgba(16,185,129,0.3)',
            animation: listening ? 'pulse 1s infinite' : 'none',
          }}
        >
          {listening ? '🔴' : '🎤'}
        </button>
        <div style={{ marginTop: 12, opacity: 0.8 }}>
          {listening ? 'Слушаю...' : 'Нажмите и говорите'}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>

      {/* Error */}
      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.2)',
          borderRadius: 8,
          padding: 12,
          marginBottom: 16,
          textAlign: 'center',
        }}>
          {error}
        </div>
      )}

      {/* Transcript */}
      {transcript && (
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 16,
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: 8 }}>📝 Распознано:</div>
          <div style={{ fontStyle: 'italic', opacity: 0.8 }}>"{transcript}"</div>
        </div>
      )}

      {/* Parsed Data */}
      {parsed && (
        <div style={{
          background: 'rgba(16,185,129,0.1)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 16,
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: 12 }}>📄 Данные фактуры:</div>
          <table style={{ width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ opacity: 0.7, padding: '4px 0' }}>Клиент:</td>
                <td style={{ fontWeight: 'bold' }}>{parsed.clientName || '—'}</td>
              </tr>
              <tr>
                <td style={{ opacity: 0.7, padding: '4px 0' }}>Услуга:</td>
                <td style={{ fontWeight: 'bold' }}>{parsed.serviceName || '—'}</td>
              </tr>
              <tr>
                <td style={{ opacity: 0.7, padding: '4px 0' }}>Сумма:</td>
                <td style={{ fontWeight: 'bold', color: '#10b981', fontSize: 18 }}>
                  {parsed.amount ? `${parsed.amount} ${parsed.currency}` : '—'}
                </td>
              </tr>
            </tbody>
          </table>
          
          <button
            className="btn btn-primary"
            style={{ width: '100%', marginTop: 16 }}
            onClick={createInvoice}
            disabled={!parsed.amount}
          >
            📄 Создать проформу
          </button>
        </div>
      )}
    </div>
  );
}
